import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import {
  searchLocatorsAsync,
  clearMessages,
  selectSearchRows,
  selectSearchLoading,
  selectSearchError,
  selectSearchTotal,
} from '../../redux/locatorManagement/searchLocatorsSlice';

const DEFAULT_FILTERS = { nameOrValue: '', platform: '', build: '', page: '', market_id: '' };
const DEBOUNCE_MS = 400;

export default function useLocatorsSearch() {
  const dispatch = useDispatch();
  const form = useForm({ defaultValues: DEFAULT_FILTERS });

  const rows = useSelector(selectSearchRows);
  const loading = useSelector(selectSearchLoading);
  const error = useSelector(selectSearchError);

  const [pageNumber, setPageNumber] = useState(0); // zero-indexed
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [sortBy, setSortBy] = useState('locator_name');
  const [sortOrder, setSortOrder] = useState('asc');

  const total = useSelector(selectSearchTotal);

  const buildParams = (
    { nameOrValue, platform, build, page, market_id },
    { pageNumber, rowsPerPage, sortBy, sortOrder } = {},
  ) => {
    const params = {
      name_or_value: nameOrValue,
      platform: platform || undefined,
      build: build !== '' ? Number(build) : undefined,
      page: page || undefined,
      market_id: market_id || undefined,
    };
    if (rowsPerPage != null) params.limit = rowsPerPage;
    if (pageNumber != null) params.offset = pageNumber * (rowsPerPage || 0);
    if (sortBy) params.sort_by = sortBy;
    if (sortOrder) params.sort_order = sortOrder;
    return params;
  };

  const values = form.watch();
  const timerRef = useRef(null);

  useEffect(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      dispatch(
        searchLocatorsAsync(buildParams(values, { pageNumber, rowsPerPage, sortBy, sortOrder })),
      );
    }, DEBOUNCE_MS);

    return () => clearTimeout(timerRef.current);
  }, [
    values.nameOrValue,
    values.platform,
    values.build,
    values.page,
    values.market_id,
    pageNumber,
    rowsPerPage,
    sortBy,
    sortOrder,
    dispatch,
  ]);

  const handleClear = () => {
    form.reset(DEFAULT_FILTERS);
    setPageNumber(0);
    dispatch(clearMessages());
  };

  const refetch = () => {
    dispatch(
      searchLocatorsAsync(
        buildParams(form.getValues(), { pageNumber, rowsPerPage, sortBy, sortOrder }),
      ),
    );
  };

  return {
    form,
    rows,
    loading,
    error,
    handleClear,
    refetch,
    clearMessages: () => dispatch(clearMessages()),
    // pagination + sorting
    pageNumber,
    setPageNumber,
    rowsPerPage,
    setRowsPerPage,
    total,
    sortBy,
    sortOrder,
    setSortBy,
    setSortOrder,
  };
}
